import React, { useState } from "react";
import { X, Check, Sparkles, Crown, Zap, ChevronRight, Loader2, Tag } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useBilling } from "../context/BillingContext";

const PLANS = [
  {
    id: "pro_monthly",
    name: "Pro Monthly",
    price: "$9",
    period: "/month",
    icon: Zap,
    accent: "paars",
    blurb: "Unlock the full tracker. Cancel any time.",
    cta: "Start Pro Monthly",
  },
  {
    id: "pro_yearly",
    name: "Pro Yearly",
    price: "$79",
    period: "/year",
    savings: "Save $29",
    icon: Sparkles,
    accent: "groen",
    blurb: "Two months free vs monthly. Best value.",
    cta: "Start Pro Yearly",
    recommended: true,
  },
  {
    id: "founder_lifetime",
    name: "Founder Lifetime",
    price: "$149",
    period: "once",
    badge: "Limited · 100 seats",
    icon: Crown,
    accent: "gold",
    blurb: "Pro forever. Locks in current pricing. Supports the project.",
    cta: "Become a Founder",
  },
];

const FEATURES = [
  { label: "Holdings", free: "5", pro: "Unlimited" },
  { label: "Master Catalog access", free: "✓", pro: "✓" },
  { label: "YOC / YTM calcs", free: "✓", pro: "✓" },
  { label: "Broker auto-sync · LIVE (IBKR, Mexem, Schwab, +30)", free: "—", pro: "✓" },
  { label: "Email alerts (coupon T-7/T-3)", free: "—", pro: "✓" },
  { label: "Weekly portfolio digest", free: "—", pro: "✓" },
  { label: "Saved filter hotlists", free: "—", pro: "✓" },
  { label: "Near-realtime prices", free: "—", pro: "✓" },
  { label: "CSV import / export", free: "✓", pro: "✓" },
];

export default function UpgradeModal() {
  const { upgradeModal, closeUpgrade, freeHoldingLimit } = useBilling();
  const { open, reason } = upgradeModal;
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoValidating, setPromoValidating] = useState(false);
  // appliedPromo: { code, discount_amount, original_amount, final_amount, remaining_uses, plan_id }
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState(null);

  if (!open) return null;

  const validatePromo = async (planIdForValidation, codeOverride) => {
    const code = (codeOverride ?? promoCode).trim().toUpperCase();
    if (!code) return;
    setPromoValidating(true);
    setPromoError(null);
    try {
      const { data } = await api.post("/billing/promo/validate", {
        code,
        plan_id: planIdForValidation,
      });
      setPromoCode(code);
      setAppliedPromo({ ...data, plan_id: planIdForValidation });
      toast.success(`Promo applied: -$${data.discount_amount.toFixed(2)}`);
    } catch (e) {
      setAppliedPromo(null);
      setPromoError(e?.response?.data?.detail || "Invalid code");
    } finally {
      setPromoValidating(false);
    }
  };

  const clearPromo = () => {
    setPromoCode("");
    setAppliedPromo(null);
    setPromoError(null);
  };

  const startCheckout = async (planId) => {
    setLoadingPlan(planId);
    try {
      const promoToSend = appliedPromo && appliedPromo.plan_id === planId ? appliedPromo.code : undefined;
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
        ...(promoToSend ? { promo_code: promoToSend } : {}),
      });
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoadingPlan(null);
    }
  };

  const reasonText =
    reason === "free_tier_limit_reached"
      ? `You've hit the free tier limit of ${freeHoldingLimit} holdings.`
      : "Upgrade to unlock the full tracker.";

  return (
    <div
      data-testid="upgrade-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-graphite/80 backdrop-blur-sm"
      onClick={closeUpgrade}
    >
      <div
        className="relative w-full max-w-5xl bg-champagne border border-paars-dark/30 max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="upgrade-modal-close"
          onClick={closeUpgrade}
          className="absolute top-4 right-4 z-20 text-stone hover:text-champagne transition-colors p-2 bg-graphite/60 hover:bg-graphite rounded-full"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Hero */}
        <div className="bg-graphite px-10 py-10 border-b border-paars-dark/40 relative overflow-hidden">
          <div className="absolute inset-0 terminal-grid opacity-15 pointer-events-none" />
          <div className="absolute -right-16 -top-16 w-72 h-72 bg-paars/15 blur-3xl rounded-full pointer-events-none" />
          <div className="relative z-10">
            <div className="text-[10px] tracking-[0.35em] uppercase text-paars font-bold mb-2">// UPGRADE_TO_PRO</div>
            <h2 className="text-3xl font-bold text-champagne tracking-tight">
              {reasonText}
            </h2>
            <p className="mt-3 text-sm text-stone/70 max-w-xl">
              Pro removes the holdings cap and unlocks email alerts, weekly digest, hotlists and
              near-realtime prices. Pick a plan below.
            </p>
          </div>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-px bg-stone/60">
          {PLANS.map((p) => {
            const Icon = p.icon;
            const isLoading = loadingPlan === p.id;
            const promoApplies = appliedPromo && appliedPromo.plan_id === p.id;
            return (
              <div
                key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={`relative bg-champagne p-8 flex flex-col ${p.recommended ? "ring-2 ring-paars" : ""}`}
              >
                {p.recommended && (
                  <div className="absolute top-0 right-0 bg-paars text-graphite text-[10px] tracking-[0.25em] uppercase font-bold px-3 py-1">
                    Best value
                  </div>
                )}
                {p.badge && (
                  <div className="absolute top-0 right-0 bg-gold text-graphite text-[10px] tracking-[0.25em] uppercase font-bold px-3 py-1">
                    {p.badge}
                  </div>
                )}
                <div className={`w-10 h-10 flex items-center justify-center mb-5 bg-${p.accent}`}>
                  <Icon className="w-5 h-5 text-graphite" strokeWidth={2.2} />
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold mb-2">// {p.id.toUpperCase()}</div>
                <h3 className="text-xl font-bold text-graphite tracking-tight mb-2">{p.name}</h3>
                <div className="flex items-baseline gap-2 mb-3">
                  {promoApplies ? (
                    <>
                      <span className="text-4xl font-bold text-groen-dark" data-testid={`discounted-price-${p.id}`}>
                        ${appliedPromo.final_amount.toFixed(0)}
                      </span>
                      <span className="text-base text-graphite/45 line-through font-mono">{p.price}</span>
                      <span className="text-sm text-graphite/55 font-mono">{p.period}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-graphite">{p.price}</span>
                      <span className="text-sm text-graphite/55 font-mono">{p.period}</span>
                    </>
                  )}
                </div>
                {promoApplies && (
                  <div className="text-[11px] text-groen-dark font-semibold mb-3 tracking-wider uppercase">
                    {appliedPromo.code} · -${appliedPromo.discount_amount.toFixed(2)}
                  </div>
                )}
                {!promoApplies && p.savings && (
                  <div className="text-xs text-groen-dark font-semibold mb-3">{p.savings}</div>
                )}
                <p className="text-xs text-graphite/65 leading-relaxed mb-6 min-h-[40px]">{p.blurb}</p>
                {p.id === "founder_lifetime" && !promoApplies && (
                  <button
                    data-testid="hint-promo-founder25"
                    onClick={() => { setPromoOpen(true); validatePromo("founder_lifetime", "FOUNDER25"); }}
                    className="text-[10px] tracking-widest uppercase text-gold hover:text-graphite border border-gold/40 hover:bg-gold/15 px-3 py-1.5 mb-3 self-start transition-colors"
                  >
                    Use code FOUNDER25 → $25 off
                  </button>
                )}
                <button
                  data-testid={`buy-${p.id}`}
                  onClick={() => startCheckout(p.id)}
                  disabled={isLoading}
                  className="mt-auto w-full px-4 py-3 bg-graphite text-champagne text-xs font-semibold tracking-wider uppercase hover:bg-graphite/90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecting…
                    </>
                  ) : (
                    <>
                      {p.cta} <ChevronRight className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Promo code */}
        <div className="border-t border-stone px-10 py-5 bg-stone/30">
          {!promoOpen && !appliedPromo && (
            <button
              data-testid="open-promo-input"
              onClick={() => setPromoOpen(true)}
              className="flex items-center gap-2 text-xs text-graphite/65 hover:text-paars-dark transition-colors font-mono tracking-wider uppercase"
            >
              <Tag className="w-3.5 h-3.5" /> Have a promo code?
            </button>
          )}
          {(promoOpen || appliedPromo) && (
            <div data-testid="promo-input-row" className="flex items-center gap-3 flex-wrap">
              <Tag className="w-4 h-4 text-paars-dark" />
              <input
                data-testid="promo-code-input"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="ENTER CODE"
                disabled={!!appliedPromo}
                className="flex-1 min-w-[160px] px-3 py-2 bg-champagne border border-stone text-graphite font-mono text-sm tracking-widest uppercase focus:outline-none focus:border-paars-dark disabled:opacity-60"
              />
              {!appliedPromo && (
                <>
                  {PLANS.map((p) => (
                    <button
                      key={p.id}
                      data-testid={`apply-promo-${p.id}`}
                      onClick={() => validatePromo(p.id)}
                      disabled={promoValidating || !promoCode.trim()}
                      className="px-3 py-2 text-[10px] tracking-widest uppercase font-bold border border-paars-dark text-paars-dark hover:bg-paars/15 disabled:opacity-40"
                    >
                      Apply to {p.name.split(" ")[1] || p.name}
                    </button>
                  ))}
                </>
              )}
              {appliedPromo && (
                <>
                  <span className="text-xs font-mono text-groen-dark font-semibold">
                    -${appliedPromo.discount_amount.toFixed(2)} on {appliedPromo.plan_id} · {appliedPromo.remaining_uses} left
                  </span>
                  <button
                    data-testid="remove-promo"
                    onClick={clearPromo}
                    className="text-xs text-graphite/55 hover:text-burgundy transition-colors uppercase tracking-wider"
                  >
                    Remove
                  </button>
                </>
              )}
              {promoError && (
                <div data-testid="promo-error" className="text-xs text-burgundy font-mono">{promoError}</div>
              )}
            </div>
          )}
        </div>

        {/* Feature matrix */}
        <div className="px-10 py-8 border-t border-stone">
          <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold mb-4">// WHAT YOU GET</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone">
                <th className="text-left py-2 text-[11px] tracking-[0.2em] uppercase text-graphite/55 font-bold">Feature</th>
                <th className="text-center py-2 text-[11px] tracking-[0.2em] uppercase text-graphite/55 font-bold w-32">Free</th>
                <th className="text-center py-2 text-[11px] tracking-[0.2em] uppercase text-paars-dark font-bold w-32">Pro / Founder</th>
              </tr>
            </thead>
            <tbody>
              {FEATURES.map((f) => (
                <tr key={f.label} className="border-b border-stone/40">
                  <td className="py-2.5 text-graphite/80">{f.label}</td>
                  <td className="py-2.5 text-center font-mono text-graphite/55 text-xs">{f.free}</td>
                  <td className="py-2.5 text-center font-mono text-paars-dark text-xs font-semibold">
                    {f.pro === "✓" ? <Check className="w-4 h-4 inline text-groen-dark" /> : f.pro}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-6 text-[10px] font-mono text-graphite/45 tracking-widest">
            PAYMENTS BY STRIPE · CANCEL ANY TIME · NO HIDDEN FEES
          </div>
        </div>
      </div>
    </div>
  );
}
